import { useState, useEffect } from "react";
import { getAggregatedFeedback } from "../service/feedbackBlog";
import BasePage from "../components/BasePage";
import BlogCard from "../components/BlogCard";
import { blogPosts, BlogPost } from "../data/BlogPosts";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";
import Hero from "../components/Hero";
import { motion } from "framer-motion";
import FollowAs from "../components/footer/FollowAs";

interface AggregatedFeedback {
  title: string;
  likes: number;
  dislikes: number;
  imgUrl?: string;
}

const BlogPage: React.FC = () => {
  const [visiblePosts, setVisiblePosts] = useState(3);
  const [filter, setFilter] = useState<"recent" | "oldest" | "relevant">(
    "recent"
  );
  const [postsWithFeedback, setPostsWithFeedback] = useState<
    (BlogPost & AggregatedFeedback)[]
  >([]);

  useEffect(() => {
    const fetchAllFeedback = async () => {
      const feedback: AggregatedFeedback[] = await getAggregatedFeedback();
      const merged = blogPosts.map((post) => {
        const fb = feedback.find((f) => f.title === post.title);
        return {
          ...post,
          likes: fb ? fb.likes : 0,
          dislikes: fb ? fb.dislikes : 0,
        };
      });
      setPostsWithFeedback(merged);
    };
    fetchAllFeedback();
  }, []);

  const sortedPosts = [...postsWithFeedback].sort((a, b) => {
    if (filter === "recent") {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
    if (filter === "oldest") {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    }
    if (filter === "relevant") {
      return b.likes - a.likes;
    }
    return 0;
  });

  return (
    <BasePage
      title="Blog - UcWaves"
      description="Stay updated with insights on decentralized finance, blockchain innovations, and tokenomics."
      keywords="Blog, UcWaves, DeFi, blockchain, tokenomics, NFTs, smart contracts"
    >
      {/* Hero Section */}
      <div className="relative">
        <Hero />
      </div>
      <main>
        <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
          {/* Decorative images and title unchanged */}
          <img
            src={imgL}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute left-4 md:left-16 xl:left-32 top-5 -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          <img
            src={imgR}
            alt=""
            aria-hidden="true"
            className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
          />
          <div className="relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <h1 className="text-6xl md:text-[64px] font-bold md:mt-15 text-secondary-light text-center shadow-red-700 mb-32">
                Insights & News
              </h1>
            </motion.div>
            <div className="flex flex-col items-center justify-center mt-8 bg-blue-900/30 backdrop-blur-sm rounded-lg p-6 shadow-lg">
              <FollowAs />
            </div>
          </div>
        </section>

        {/* Filters – centered */}
        <div className="mb-8 w-full max-w-md mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 px-4 sm:px-0">
          {/* Desktop view */}
          <div className="hidden sm:flex items-center gap-6 mt-5">
            {["recent", "oldest", "relevant"].map((item) => (
              <label key={item} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="filter"
                  value={item}
                  checked={filter === item}
                  onChange={() =>
                    setFilter(item as "recent" | "oldest" | "relevant")
                  }
                  className="accent-[var(--color-primary)] w-4 h-4"
                />
                <span
                  className={`text-sm ${
                    filter === item
                      ? "text-[var(--color-primary-light)] font-semibold"
                      : "text-[var(--color-primary-light)]"
                  }`}
                >
                  {item === "recent" && "Most Recent"}
                  {item === "oldest" && "Oldest"}
                  {item === "relevant" && "Most Relevant"}
                </span>
              </label>
            ))}
          </div>
          {/* Mobile dropdown */}
          <div className="sm:hidden w-full max-w-xs mx-auto">
            <select
              value={filter}
              onChange={(e) =>
                setFilter(e.target.value as "recent" | "oldest" | "relevant")
              }
              className="w-full px-4 py-2 border rounded bg-white text-[var(--color-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="recent">Most Recent</option>
              <option value="oldest">Oldest</option>
              <option value="relevant">Most Relevant</option>
            </select>
          </div>
        </div>

        {/* Blog Grid – centered */}
        <section className="w-full max-w-10xl mx-auto flex flex-wrap justify-center items-stretch gap-6 px-4 pb-5">
          {sortedPosts.slice(0, visiblePosts).map((post) => (
            <BlogCard
              key={post.title}
              title={post.title}
              content={post.content}
              likes={post.likes}
              dislikes={post.dislikes}
              imageUrl={post.imgUrl || ""}
            />
          ))}
        </section>

        {visiblePosts < sortedPosts.length ? (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setVisiblePosts((prev) => prev + 3)}
              className="px-8 py-3 bg-blue-900 text-white text-lg  transition hover:bg-blue-800 hover:-translate-y-1 mb-5"
            >
              Load More
            </button>
          </div>
        ) : (
          <p className="mt-8 text-[var(--color-text-light)] text-sm opacity-70 text-center">
            No more articles available.
          </p>
        )}
      </main>
    </BasePage>
  );
};

export default BlogPage;
